import { useState } from "react"
import { Link, Navigate, useNavigate } from "react-router"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { ApiError } from "@/api/client"
import { AUTH_ME_QUERY_KEY, useRegister } from "@/api/hooks/auth"
import { useAuth } from "@/auth/AuthProvider"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

const schema = z
  .object({
    username: z.string().trim().min(3, "Username must be at least 3 characters"),
    email: z.string().trim().email("Enter a valid email"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match!",
  })

type RegisterFormValues = z.infer<typeof schema>

/**
 * React port of `templates/register.html` (T3.10). Public route — sits
 * outside `<AppShell/>` and `<ProtectedRoute/>`. Cookie-mode auth via
 * `useRegister()`; the backend's `/auth/register` shares the same
 * `_issue_auth_response` as `/auth/login`, so a successful POST sets
 * the auth cookie and we just refetch `/auth/me` and navigate.
 *
 * Parity-fixed gotchas from `docs/migration/parity/register.md`:
 *   - Submit button is disabled while the mutation is in flight.
 *   - Already-authenticated visitors get `<Navigate/>`'d before the
 *     form is rendered (parity-flash fix).
 *   - `autocomplete` attrs added for password manager support.
 *   - The source's 1s `setTimeout` UX delay is dropped.
 */
export default function RegisterPage() {
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const registerMutation = useRegister()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  })

  if (authLoading) return null
  if (user) return <Navigate to="/dashboard" replace />

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    try {
      await registerMutation.mutateAsync({
        username: values.username,
        email: values.email,
        password: values.password,
      })
      await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY })
      navigate("/dashboard", { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message)
      } else {
        setSubmitError("Network error. Please try again.")
      }
    }
  })

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg text-brand-fg px-4">
      <Card className="w-full max-w-md bg-[#3a3a3a] border-[#4a4a4a] rounded-2xl p-12 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
        <h1 className="text-center text-3xl font-semibold text-brand-fg mb-2">
          🚀 Get Started
        </h1>
        <p className="text-center text-sm text-[#b0b0b0] mb-8">
          Create your TikTok Autofill account
        </p>

        {submitError ? (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-[#663333] bg-[#4a2a2a] px-3 py-3 text-sm text-[#f87171]"
          >
            {submitError}
          </div>
        ) : null}

        <form onSubmit={onSubmit} noValidate>
          <div className="mb-6">
            <Label
              htmlFor="username"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Username
            </Label>
            <Input
              id="username"
              type="text"
              autoComplete="username"
              aria-invalid={errors.username ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("username")}
            />
            {errors.username ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.username.message}
              </p>
            ) : null}
          </div>

          <div className="mb-6">
            <Label
              htmlFor="email"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Email
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={errors.email ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("email")}
            />
            {errors.email ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.email.message}
              </p>
            ) : null}
          </div>

          <div className="mb-6">
            <Label
              htmlFor="password"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.password ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("password")}
            />
            {errors.password ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.password.message}
              </p>
            ) : null}
          </div>

          <div className="mb-6">
            <Label
              htmlFor="confirmPassword"
              className="mb-2 block text-brand-fg font-semibold"
            >
              Confirm Password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              aria-invalid={errors.confirmPassword ? "true" : "false"}
              className="h-12 rounded-lg border-2 border-[#555555] bg-[#2d2d2d] px-3 text-base text-brand-fg focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-brand-accent"
              {...register("confirmPassword")}
            />
            {errors.confirmPassword ? (
              <p className="mt-1 text-sm text-[#f87171]">
                {errors.confirmPassword.message}
              </p>
            ) : null}
          </div>

          <Button
            type="submit"
            disabled={registerMutation.isPending}
            className="w-full h-12 rounded-lg text-base font-semibold text-white border-0 bg-gradient-to-br from-[#8b9aff] to-[#667eea] hover:opacity-95 hover:-translate-y-0.5 hover:shadow-[0_10px_20px_rgba(139,154,255,0.4)] transition-all"
          >
            {registerMutation.isPending ? "Creating account…" : "Create Account"}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-[#b0b0b0]">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-brand-accent hover:underline"
          >
            Login
          </Link>
        </div>
      </Card>
    </div>
  )
}

export { RegisterPage }
